/*------------------------------------------------------------------------------
   About      : NBI Insights landing page (epic DA-697, II-FE-2)

                Under Content Management, a sibling of Recommendations, inside
                the Manage Project shell. Pilot-scoped: renders under
                CMSPilotLayout.

                Cloned from `config-registry/components/ConfigRegistryPage.tsx`,
                including its `requestSeqRef` stale-response guard, with paging
                moved client-side because `getInsights` has no paging params —
                it returns the whole pilot partitioned into draft / modified /
                published, which `mergeInsightPartitions` flattens.

                Reference mockup is `image-5.png`. `image-4.png` is SUPERSEDED —
                it predates the naming decision (it is titled "Interesting
                Insights", not "NBI Insights") and shows a "Copy from Master"
                button that is not a feature. Do not build from it.

   Copyright (c) 2026 Bidgely. All rights reserved.
------------------------------------------------------------------------------*/
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Alert, Badge, Box, Button, Chip, InputAdornment, Stack,
         TextField, Typography } from '@mui/material'
import AddIcon        from '@mui/icons-material/Add'
import FilterListIcon from '@mui/icons-material/FilterList'
import SearchIcon     from '@mui/icons-material/Search'
import { useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'

import { useGlobalConfig }             from 'context/GlobalContext'
import { ANALYTICS_EVENTS }            from 'framework/analytics/AnalyticsEvents'
import { UI_PAGES }                    from 'framework/access/UiRegistry'
import { BFF_FEATURES }                from 'framework/access/BffFeatureCatalog'
import { PermissionGate }              from 'components/access/PermissionGate'
import { usePermissions }              from 'hooks/usePermissions'
import { useAppDispatch }              from 'store/Store'
import { selectCMSSelectedPilot,
         selectCMSSelectedPilotDocId } from 'store/dataReducers/cmsPilotSlice'
import { CONTENT_EDIT_ACTION,
         CONTENT_EDIT_STATUS,
         CONTENT_VERIFICATION_STATUS } from 'types/cms/frontendTypes'

import { InsightApis }          from '../api/insightAPI'
import { useInsightCatalog }    from '../hooks/useInsightCatalog'
import { useInsightWorkflowActions } from '../hooks/useInsightWorkflowActions'
import { filterInsights }       from '../utils/insightFilters'
import { InsightsTable }        from '../components/InsightsTable'
import { EDIT_STATUS_STYLE }    from '../components/InsightStatusBadge'
import { InsightFiltersDrawer } from '../components/InsightFiltersDrawer'
import { InsightBulkActionBar } from '../components/InsightBulkActionBar'
import { ConfirmDiscardDraftsDialog } from '../components/ConfirmDiscardDraftsDialog'
import { InsightRecord }        from '../types/insightTypes'
import { clearSelection,
         resetFilters,
         selectHasActiveInsightFilters,
         selectInsightFilters,
         selectInsightPage,
         selectInsightPageSize,
         selectInsights,
         selectInsightsError,
         selectInsightsLoading,
         selectReviewWorkflow,
         selectSelectedIds,
         selectStatusLabels,
         setError,
         setFilters,
         setInsightsFromPartitions,
         setIsLoading,
         setPage,
         setPageSize,
         setReviewWorkflow,
         setSelectedIds,
         upsertInsight }        from '../store/insightUISlice'

export const InsightListPage = () => {

  const { rc }   = useGlobalConfig()
  const { can }  = usePermissions()
  const dispatch = useAppDispatch()
  const navigate = useNavigate()

  const insights         = useSelector(selectInsights)
  const filters          = useSelector(selectInsightFilters)
  const page             = useSelector(selectInsightPage)
  const pageSize         = useSelector(selectInsightPageSize)
  const selectedIds      = useSelector(selectSelectedIds)
  const isLoading        = useSelector(selectInsightsLoading)
  const loadError        = useSelector(selectInsightsError)
  const statusLabels     = useSelector(selectStatusLabels)
  const reviewWorkflow   = useSelector(selectReviewWorkflow)
  const hasActiveFilters = useSelector(selectHasActiveInsightFilters)

  const selectedPilotDocId = useSelector(selectCMSSelectedPilotDocId)
  const selectedPilot      = useSelector(selectCMSSelectedPilot)
  const pilotId            = selectedPilot?.pilotId

  const { catalog, loading : catalogLoading, error : catalogError } = useInsightCatalog(rc)
  const workflow = useInsightWorkflowActions(selectedPilotDocId, pilotId)

  const [filterDrawerOpen, setFilterDrawerOpen] = useState(false)
  const [workflowError, setWorkflowError]       = useState<string | undefined>()
  const [confirmDiscard, setConfirmDiscard]     = useState(false)

  useEffect(() => {
    rc.analytics.trackEvent(ANALYTICS_EVENTS.NBI_INSIGHTS.LIST_LOADED)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Monotonic id per request — out-of-order responses are dropped so a slow
  // response for one pilot can never overwrite a newer pilot's rows. Kept from
  // the config-registry page, where it was a documented bug fix.
  const requestSeqRef = useRef(0)

  const fetchInsights = useCallback(async (utilityDocumentId: string) => {
    const seq = ++requestSeqRef.current
    dispatch(setIsLoading(true))
    dispatch(setError(undefined))

    const resp = await InsightApis.getInsights(rc, utilityDocumentId)

    if (seq !== requestSeqRef.current) {
      return // stale response — a newer request owns the table
    }

    if (resp.error || !resp.data) {
      dispatch(setError(resp.error?.message || 'Failed to load insights'))
    } else {
      dispatch(setInsightsFromPartitions(resp.data))
    }
    dispatch(setIsLoading(false))
  }, [rc, dispatch])

  useEffect(() => {
    if (selectedPilotDocId) fetchInsights(selectedPilotDocId)
  }, [selectedPilotDocId, fetchInsights])

  // Fetched once per mount: every QA-status label in the table resolves through
  // `statusLabels`, so the column is unusable without it.
  useEffect(() => {
    let cancelled = false
    InsightApis.getReviewWorkflow(rc).then((resp) => {
      if (cancelled) return
      if (resp.error || !resp.data) {
        setWorkflowError(resp.error?.message || 'Failed to load review workflow')
        return
      }
      dispatch(setReviewWorkflow(resp.data))
    })
    return () => { cancelled = true }
  }, [rc, dispatch])

  // Filtering is local — see the module header. Memoized on the filter fields
  // rather than the object so an unrelated slice update does not re-filter.
  const visibleRows = useMemo(
    () => filterInsights(insights, filters, catalog),
    [insights, filters, catalog],
  )

  const countOf = (status: CONTENT_EDIT_STATUS) =>
    insights.filter((i) => i.editStatus === status).length

  const handleFilterChange = (patch: Parameters<typeof setFilters>[0]) => {
    dispatch(setFilters(patch))
    rc.analytics.trackEvent(ANALYTICS_EVENTS.NBI_INSIGHTS.LIST_FILTER_APPLIED, {
      filter : Object.keys(patch).join(','),
    })
  }

  /* Per-row in-flight set, so one row's toggle does not lock the whole table —
     `workflow.isRunning` is a single flag shared by the bulk bar. */
  const [busyIds, setBusyIds] = useState<string[]>([])

  const withRowBusy = async (documentId: string, run: () => Promise<unknown>) => {
    setBusyIds((prev) => [...prev, documentId])
    try { await run() }
    finally { setBusyIds((prev) => prev.filter((id) => id !== documentId)) }
  }

  const handleChangeQaStatus = useCallback(
    (insight: InsightRecord, next: CONTENT_VERIFICATION_STATUS) =>
      withRowBusy(insight.documentId, () => workflow.changeQaStatus(insight.documentId, next)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [workflow.changeQaStatus],
  )

  /**
   * Active/inactive is an ordinary content edit, so it goes through `updateAll`
   * like any other field rather than a dedicated route.
   *
   * Sends only `{ documentId, disabled }` — `code`, `insightId` and `editStatus`
   * are server-owned, and sending the rest of the record would re-assert stale
   * values for fields the user did not touch.
   */
  const handleToggleDisabled = useCallback(
    (insight: InsightRecord, disabled: boolean) => withRowBusy(insight.documentId, async () => {
      if (!selectedPilotDocId || !pilotId) return

      const resp = await InsightApis.updateAll(rc, {
        pilotId,
        data    : [{ documentId : insight.documentId, disabled }],
      })

      if (resp.error) {
        dispatch(setError(resp.error.message || 'Could not change the active state'))
        return
      }
      // A 200 can still carry per-item failures; one bad row does not fail the
      // batch, so an unexamined success would silently drop the change.
      if (resp.data?.error?.length) {
        dispatch(setError(resp.data.error
          .flatMap((entry) => Object.entries(entry).map(([k, v]) => `${k}: ${v}`))
          .join('; ')))
        return
      }

      const saved = resp.data?.insights?.find((i) => i.documentId === insight.documentId)
      // Re-seed from the server's copy: editStatus auto-demotes
      // PUBLISHED → MODIFIED on any edit, and this one is no exception.
      if (saved) dispatch(upsertInsight(saved))
      else await fetchInsights(selectedPilotDocId)
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [rc, selectedPilotDocId, pilotId, dispatch, fetchInsights],
  )

  const handleEdit = (insight: InsightRecord) => {
    rc.analytics.trackEvent(ANALYTICS_EVENTS.NBI_INSIGHTS.LIST_INSIGHT_CLICKED, {
      insight_type : insight.type,
    })
    navigate(`/cms/interesting-insight/edit/${insight.documentId}`)
  }

  /**
   * Whether a single row can be published right now.
   *
   * Driven by the workflow ACTION table rather than an inline
   * `=== READY_TO_PUBLISH` check, so the rule lives in one place and follows the
   * server: `actions[READY_TO_PUBLISH]` is `[PUBLISH]` for content publishers
   * and `[]` for the utility role, so a reviewer looking at a ready record
   * still correctly gets a disabled button.
   *
   * The BFF feature grant is folded in for the same reason the bulk bar gates
   * on it — publish/unpublish are CONTENT_PUBLISHERS-only in proxypulse, and a
   * button that 403s on click is worse than one that explains itself.
   */
  const canPublishRow = useCallback((insight: InsightRecord) => {
    if (!can(BFF_FEATURES.INSIGHTS.PUBLISH_ALL)) return false
    const permitted = reviewWorkflow?.[0]?.actions?.[insight.verificationStatus] ?? []
    return permitted.includes(CONTENT_EDIT_ACTION.PUBLISH)
  }, [can, reviewWorkflow])

  /* Routed through the same `workflow.publish` the bulk bar uses, so a failure
     surfaces in the same alert with the same per-item detail — the outcome
     banner renders independently of the selection. */
  const handlePublishRow = useCallback(
    (insight: InsightRecord) =>
      withRowBusy(insight.documentId, () => workflow.publish([insight.documentId])),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [workflow.publish],
  )

  return (
    <Box sx={{ p : 3 }}>

      {/* ── Header ─────────────────────────────────────────────────────── */}
      <Stack direction="row" alignItems="flex-start" sx={{ mb : 2 }}>
        <Box>
          <Typography variant="h5" sx={{ fontWeight : 600 }}>NBI Insights</Typography>
          <Typography variant="body2" color="text.secondary">
            Manage and validate behavioral insights across the product ecosystem.
          </Typography>
        </Box>
        <Box sx={{ flexGrow : 1 }} />
        <PermissionGate require={UI_PAGES.NBI_INSIGHTS.ctas.create.id}>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => {
              rc.analytics.trackEvent(ANALYTICS_EVENTS.NBI_INSIGHTS.CREATE_OPENED)
              navigate('/cms/interesting-insight/create')
            }}
          >
            Create New Insight
          </Button>
        </PermissionGate>
      </Stack>

      {/* ── Errors ─────────────────────────────────────────────────────── */}
      {catalogError && (
        <Alert severity="error" sx={{ mb : 2 }}>
          Catalog failed to load ({catalogError}). Labels fall back to raw codes and
          filters are unavailable until it loads.
        </Alert>
      )}
      {workflowError && (
        <Alert severity="warning" sx={{ mb : 2 }}>
          Review workflow failed to load ({workflowError}). QA statuses show their
          stored values rather than display labels.
        </Alert>
      )}
      {loadError && <Alert severity="error" sx={{ mb : 2 }}>{loadError}</Alert>}

      {!selectedPilotDocId && (
        <Alert severity="warning">Select a project to load its insights.</Alert>
      )}

      {selectedPilotDocId && (
        <>
          {/* ── Search + filters + counts ────────────────────────────── */}
          <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb : 2 }}>
            <TextField
              size="small"
              placeholder="Search insight ID, code or template text…"
              value={filters.search}
              onChange={(e) => dispatch(setFilters({ search : e.target.value }))}
              onBlur={() => filters.search && rc.analytics.trackEvent(ANALYTICS_EVENTS.NBI_INSIGHTS.LIST_SEARCHED)}
              sx={{ minWidth : 340 }}
              InputProps={{
                startAdornment : (
                  <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>
                ),
              }}
            />

            <Badge color="primary" variant="dot" invisible={!hasActiveFilters}>
              <Button
                variant="outlined"
                startIcon={<FilterListIcon />}
                onClick={() => setFilterDrawerOpen(true)}
              >
                Filters
              </Button>
            </Badge>

            {hasActiveFilters && (
              <Button size="small" onClick={() => dispatch(resetFilters())}>Clear all</Button>
            )}

            <Box sx={{ flexGrow : 1 }} />

            {/* Colours come from the same map the table's status badge uses —
                a Draft chip here and a Draft badge in the grid must not be two
                different colours. */}
            <Stack direction="row" spacing={1}>
              {([CONTENT_EDIT_STATUS.DRAFT,
                 CONTENT_EDIT_STATUS.MODIFIED,
                 CONTENT_EDIT_STATUS.PUBLISHED] as const).map((status) => (
                <Chip
                  key={status}
                  size="small"
                  color={EDIT_STATUS_STYLE[status].color}
                  label={`${EDIT_STATUS_STYLE[status].label} ${countOf(status)}`}
                />
              ))}
            </Stack>
          </Stack>

          {/* Shows the filtered count against the total, so a filter that hides
              most of the pilot is never mistaken for an empty project. */}
          {hasActiveFilters && (
            <Typography variant="caption" color="text.secondary" sx={{ display : 'block', mb : 1 }}>
              Showing {visibleRows.length} of {insights.length} insights
            </Typography>
          )}

          <InsightBulkActionBar
            selectedCount={selectedIds.length}
            isRunning={workflow.isRunning}
            outcome={workflow.outcome}
            onDismissOutcome={workflow.clearOutcome}
            onClear={() => dispatch(clearSelection())}
            onPublish={() => workflow.publish(selectedIds)}
            onUnpublish={() => workflow.unpublish(selectedIds)}
            onDiscard={() => setConfirmDiscard(true)}
          />

          {/* `discardAllDrafts` DELETES a never-published draft rather than
              reverting it, so the dialog has to name which outcome applies to
              the actual selection instead of saying "discard changes". */}
          <ConfirmDiscardDraftsDialog
            open={confirmDiscard}
            selected={insights.filter((i) => selectedIds.includes(i.documentId))}
            onCancel={() => setConfirmDiscard(false)}
            onConfirm={async () => {
              setConfirmDiscard(false)
              await workflow.discardDrafts(selectedIds)
              dispatch(clearSelection())
            }}
          />

          <InsightsTable
            rows={visibleRows}
            catalog={catalog}
            isLoading={isLoading || catalogLoading}
            page={page}
            pageSize={pageSize}
            selectedIds={selectedIds}
            statusLabels={statusLabels}
            onPageChange={(nextPage, nextPageSize) => {
              if (nextPageSize !== pageSize) dispatch(setPageSize(nextPageSize))
              else dispatch(setPage(nextPage))
            }}
            onSelectionChange={(ids) => dispatch(setSelectedIds(ids))}
            onEdit={handleEdit}
            onPublish={handlePublishRow}
            canPublish={canPublishRow}
            busyIds={busyIds}
            onChangeQaStatus={handleChangeQaStatus}
            onToggleDisabled={handleToggleDisabled}
          />

          <InsightFiltersDrawer
            open={filterDrawerOpen}
            filters={filters}
            allRows={insights}
            catalog={catalog}
            onClose={() => setFilterDrawerOpen(false)}
            onChange={handleFilterChange}
            onReset={() => dispatch(resetFilters())}
          />
        </>
      )}
    </Box>
  )
}

export default InsightListPage
